import { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./HeaderQuickLinks.css";

// Copia com fallback: a Clipboard API só existe em contexto seguro (https) e pode
// falhar em webview de celular, onde boa parte dos donos de barbearia acessa.
const copyToClipboard = async (text) => {
    if (navigator?.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            // Cai no fallback abaixo.
        }
    }

    try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);
        return copied;
    } catch (error) {
        return false;
    }
};

/**
 * Atalho fixo no cabeçalho para os links públicos da empresa.
 *
 * Antes, o link de agendamento só existia dentro do Dashboard de Agendamentos e o do
 * Portal do Colaborador exigia selecionar uma linha na lista de colaboradores e abrir
 * o menu de ações — caminho que o usuário leigo não descobre sozinho.
 *
 * @param {object} companyInfo - Resposta de Api.registration.company_info (uuid e publicSlug)
 */
const HeaderQuickLinks = ({ companyInfo }) => {
    const { t } = useTranslation();
    const [copiedKey, setCopiedKey] = useState(null);

    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    // O slug é mais amigável para compartilhar, mas o uuid funciona na mesma rota
    // (/p/:slug aceita os dois), então serve de reserva para empresa sem slug definido.
    const publicId = companyInfo?.publicSlug || companyInfo?.uuid;

    // Sem identificador não há link para montar: some com o botão em vez de mostrar
    // um menu quebrado.
    if (!publicId) return null;

    const links = [
        {
            key: "booking",
            icon: "fa-solid fa-calendar-check",
            label: t("quick_links.booking"),
            description: t("quick_links.booking_description"),
            url: `${baseUrl}/p/${publicId}`,
        },
        {
            key: "portal",
            icon: "fa-solid fa-user-tie",
            label: t("quick_links.collaborator_portal"),
            description: t("quick_links.collaborator_portal_description"),
            url: `${baseUrl}/p/${publicId}/portal/login`,
        },
    ];

    const handleCopy = async (link) => {
        const copied = await copyToClipboard(link.url);
        if (!copied) return;

        // Confirmação inline em vez de modal: copiar link é ação corriqueira e um
        // popup a cada clique atrapalharia mais do que ajuda.
        setCopiedKey(link.key);
        setTimeout(() => setCopiedKey((current) => (current === link.key ? null : current)), 2000);
    };

    return (
        <Dropdown className="colmeia-quick-links" align="end" autoClose="outside">
            <Dropdown.Toggle
                as="button"
                type="button"
                className="colmeia-theme-toggle colmeia-quick-links__toggle"
                aria-label={t("quick_links.title")}
                title={t("quick_links.title")}
            >
                <i className="fa-solid fa-link" aria-hidden="true" />
            </Dropdown.Toggle>

            <Dropdown.Menu align="end" className="colmeia-quick-links__menu">
                <div className="colmeia-quick-links__heading">{t("quick_links.title")}</div>

                {links.map((link) => (
                    <div key={link.key} className="colmeia-quick-links__item">
                        <div className="colmeia-quick-links__label">
                            <i className={link.icon} aria-hidden="true" />
                            <span>{link.label}</span>
                        </div>
                        <div className="colmeia-quick-links__description">{link.description}</div>
                        <div className="colmeia-quick-links__url" title={link.url}>{link.url}</div>

                        <div className="colmeia-quick-links__actions">
                            <button
                                type="button"
                                className="colmeia-quick-links__action"
                                onClick={() => handleCopy(link)}
                            >
                                <i
                                    className={copiedKey === link.key ? "fa-solid fa-check" : "fa-regular fa-copy"}
                                    aria-hidden="true"
                                />
                                {copiedKey === link.key ? t("copy.copied") : t("copy.copy")}
                            </button>

                            <a
                                className="colmeia-quick-links__action"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                                {t("quick_links.open")}
                            </a>
                        </div>
                    </div>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default HeaderQuickLinks;
